import { useEffect, useRef, useState } from "react";

import EnumCreatorType from "@wallet-manager/node-types/dist/src/postgres/const/CreatorType";
import EnumRequestStatus from "@wallet-manager/node-types/dist/types/ledger/enums/LedgerRequestStatus";

import { getCrossMerchantTransferRequestExport } from "../../../api/merchant";
import { Ipage } from "../../../api/types";
import { AssetMultiSelection } from "../../../components/AssetSelection";
import { ChainSingleSelection } from "../../../components/ChainSelection";
import { useDatePicker } from "../../../components/DatePicker";
import { useAssets, useChains } from "../../../components/FetchConfig";
import GeneralBtn from "../../../components/GeneralBtn";
import {
  NodeTypesEnumMultiSelection,
  NodeTypesEnumSingleSelection,
} from "../../../components/GeneralSelection";
import MpTextField from "../../../components/MpTextField";
import MpTextFieldMultiple from "../../../components/MpTextFieldMultiple";
import {
  Box,
  Container,
  SelectChangeEvent,
} from "../../../components/MuiGenerals";
import TableTab from "../../../components/TableTab";
import { useAlerting, usePermission, useTranslation } from "../../../hooks";
import { selectCrossMerchant } from "../../../reducer/crossMerchantSlice";
import { useAppSelector } from "../../../reducer/hooks";
import { selectProfile } from "../../../reducer/profileSlice";
import { FeatureCodes } from "../../../utils/constant";
import {
  displayAmount,
  downloadFiles,
  findChainInfo,
  findDecimalByChainNameAndAsset,
  getFullApiResponse,
  listMappingTransform,
  toDisplayTime,
} from "../../../utils/helper";
import { DivideLine, genField } from "../../../utils/HelperComp";
import { customSx } from "../../../utils/styling";
import { createZusInstance, useZusDialogStore } from "../../../zustand/store";
import { exportApprovalDetailConverter } from "../commonFunction";
import CrossMerchantTransferRequestList from "./CrossMerchantTransferRequestList";
import LPMerchantSingleSelection from "../../../components/Common/CrossMerchant/LPMerchantSingleSelection";

const translatePrefix = "crossMerchantTransferRequest";

enum EnumCreatorTypeForDisplay {
  Portal = EnumCreatorType.Portal,
  Api = EnumCreatorType.Api,
}

interface Ifields {
  fromChainName: string;
  toChainName: string;
  fromAssetNames: string[];
  toAssetNames: string[];
  fromClientId: string;
  toClientId: string;
  merchantOrderId: string;
  requestStatuses: string[];
  creatorType: string;
  toMerchantIds: string[];
  merchantLp: string;
  liquidityProviders: string[];
}
interface ZusParamsFace {
  toMerchantIds: string[];
  fromChainId: string | undefined;
  toChainId: string | undefined;
  fromChainType: number | undefined;
  toChainType: number | undefined;
  fromAssetNames: string[];
  toAssetNames: string[];
  fromClientIds: string[];
  toClientIds: string[];
  merchantOrderIds: string[];
  statuses: string[];
  creatorType: string;
  liquidityProviders: string[];

  dateCreatedFrom: string;
  dateCreatedTo: string;
  dateLastModifiedFrom: string;
  dateLastModifiedTo: string;
  pageSize: number;
  page: number;
}

const initFields = {
  fromChainName: "",
  toChainName: "",
  fromAssetNames: [] as string[],
  toAssetNames: [] as string[],
  fromClientId: "",
  toClientId: "",
  merchantOrderId: "",
  requestStatuses: [] as string[],
  creatorType: "",
  toMerchantIds: [] as string[],
  merchantLp: "",
  liquidityProviders: [] as string[],
} as const;
const initZusParams = {
  page: 0,
  pageSize: 20,
} as const;
export const useZusParams = createZusInstance<Ipage & Partial<ZusParamsFace>>(
  initZusParams
);
const Pkey = FeatureCodes.ledger.CrossMerchantTransferRequest;

export const reversedEnumFunction: (
  enumObj: Record<string, string>
) => Record<string, string> = (enumObj) =>
  Object.fromEntries(
    Object.entries(enumObj).map(([key, value]) => [value, key])
  );

export default function CrossMerchantTransferRequest() {
  const zusParams = useZusParams();
  const { alerting } = useAlerting();
  const { t, te, tc } = useTranslation(translatePrefix);
  const zusDialog = useZusDialogStore();
  const { hasPermission } = usePermission();
  const { merchantMapping } = useAppSelector(selectCrossMerchant);

  const [count, setCount] = useState(0);
  const onExport = async () => {
    if (count === 0) {
      return alerting("error", tc("no_data_export"));
    }
    const apiFn = (page: number, pageSize: number, signal: any) =>
      getCrossMerchantTransferRequestExport(
        { ...zusParams.body, page, pageSize },
        { signal }
      );
    const rawRes = await getFullApiResponse(apiFn, count, true);
    if (rawRes.length === 0) return;
    const omitKeys = ["rawData"];
    const totalApprovalLevel = 1;
    const res = listMapping("name", rawRes, {
      t,
      te,
      omitKeys,
      merchantMapping,
    }).map((item, rawResIndex) => ({
      ...item,
      ...exportApprovalDetailConverter(
        "cross_merchant_transfer",
        totalApprovalLevel,
        rawRes,
        rawResIndex,
        t,
        te
      ),
    }));
    const config = {};
    downloadFiles(`Cross Merchant Transfer Request`, res, config);
  };

  useEffect(() => {
    return () => {
      zusDialog.close();
      zusDialog.closeAll();
    };
  }, []);

  return (
    <Box
      sx={{
        backgroundColor: "white",
        paddingTop: "2rem",
        paddingLeft: "2rem",
        paddingRight: "2rem",
      }}
    >
      <Container disableGutters maxWidth={false}>
        <FilterBar />
      </Container>
      <DivideLine />
      <Container
        style={customSx.datagridContainer}
        maxWidth={false}
        disableGutters
      >
        <Box sx={customSx.tableExportBar}>
          {hasPermission(Pkey.Export) && (
            <GeneralBtn label="export" onClick={onExport} />
          )}
        </Box>
        <TableTab>
          <CrossMerchantTransferRequestList {...{ listMapping, setCount }} />
        </TableTab>
      </Container>
    </Box>
  );
}

export function FilterBar() {
  const merchantFromInputRef = useRef<HTMLInputElement>(null);
  const [fields, setFields] = useState<Ifields>(initFields);
  const { t } = useTranslation(translatePrefix);
  const { merchantId } = useAppSelector(selectProfile);
  const { merchantMapping, liquidityProvidersMapping } =
    useAppSelector(selectCrossMerchant);
  const zusParams = useZusParams();
  const { selectChainByPieces } = useChains();
  const { selectAssetNamesByChain } = useAssets();
  const DateObj = {
    CreationTime: useDatePicker(),
    LastModifiedDate: useDatePicker(),
  };

  const getParams: () => ZusParamsFace = () => {
    const {
      fromChainName,
      toChainName,
      fromAssetNames,
      toAssetNames,
      requestStatuses: statuses,
      creatorType,
      liquidityProviders,
      toMerchantIds,
      merchantLp,
      ...rest
    } = fields;
    const transformFieldFromStrToArr = (fieldValue: string) => {
      const rawArr = fieldValue.split(",");

      const fieldValueArr = rawArr
        .filter((item) => item)
        .map((item) => item.trim());
      return fieldValueArr;
    };
    const {
      fromClientId: fromClientIds,
      toClientId: toClientIds,
      merchantOrderId: merchantOrderIds,
    } = Object.fromEntries(
      Object.entries(rest).map(([key, value]) => [
        key,
        transformFieldFromStrToArr(value),
      ])
    );

    const { chain_id: fromChainId, chain_type: fromChainType } =
      selectChainByPieces({ chain_name: fromChainName });
    const { chain_id: toChainId, chain_type: toChainType } =
      selectChainByPieces({ chain_name: toChainName });

    const { CreationTime, LastModifiedDate } = DateObj;
    const { start: dateCreatedFrom, end: dateCreatedTo } = CreationTime;
    const { start: dateLastModifiedFrom, end: dateLastModifiedTo } =
      LastModifiedDate;

    return {
      ...initZusParams,
      fromChainId,
      toChainId,
      fromChainType,
      toChainType,
      fromAssetNames,
      toAssetNames,
      fromClientIds,
      toClientIds,
      merchantOrderIds,
      statuses,
      creatorType,
      liquidityProviderMerchantId: merchantLp,
      liquidityProviders,
      toMerchantIds,
      dateCreatedFrom,
      dateCreatedTo,
      dateLastModifiedFrom,
      dateLastModifiedTo,
    };
  };

  const apiParams = getParams();

  const onChange =
    (type: keyof Ifields) =>
    (
      e:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | SelectChangeEvent<string>
    ) => {
      const value = e.target.value;
      setFields((fields) => ({ ...fields, [type]: value }));
    };

  const onSearch = () => {
    zusParams.setBody({ ...apiParams, page: 0 });
    zusParams.refetch();
  };

  const onReset = () => {
    setFields(initFields);
    DateObj.CreationTime.clearDate();
    DateObj.LastModifiedDate.clearDate();
  };

  const allFromAssetNames = selectAssetNamesByChain({
    chain_name: fields.fromChainName,
  });
  const allToAssetNames = selectAssetNamesByChain({
    chain_name: fields.toChainName,
  });

  useEffect(() => {
    if (!merchantId || !merchantFromInputRef.current) {
      return;
    }

    merchantFromInputRef.current.value = String(
      merchantMapping[merchantId] || ""
    );
  }, [merchantMapping]);

  const F = genField({ t }, [
    ["creation_time", <DateObj.CreationTime.Picker type="dateTime" />],
    ["last_modified_time", <DateObj.LastModifiedDate.Picker type="dateTime" />],
    [
      "merchant_from",
      <MpTextField
        defaultValue={merchantId}
        inputRef={merchantFromInputRef}
        disabled
        sx={{
          input: {
            WebkitTextFillColor: "#000!important",
            color: "#000!important",
          },
          div: { backgroundColor: "#fff!important" },
        }}
      />,
    ],
    [
      "merchant_to",

      <NodeTypesEnumMultiSelection
        label={t("ph_merchant_to")}
        onChange={(toMerchantIds) =>
          setFields((f) => ({ ...f, toMerchantIds }))
        }
        value={fields.toMerchantIds}
        enumData={merchantMapping}
        nameFn={(name) => name}
        isNoSorting
      />,
    ],
    [
      "client_id_from",
      <MpTextFieldMultiple
        value={fields.fromClientId}
        onChange={onChange("fromClientId")}
        label={t("ph_client_id_from")}
        count={apiParams.fromClientIds.length}
      />,
    ],
    [
      "client_id_to",
      <MpTextFieldMultiple
        value={fields.toClientId}
        onChange={onChange("toClientId")}
        label={t("ph_client_id_to")}
        count={apiParams.toClientIds.length}
      />,
    ],
    [
      "chain_name_from",
      <ChainSingleSelection
        label={t("ph_chain_name_from")}
        setChoice={(fromChainName) =>
          setFields((f) => ({ ...f, fromChainName, fromAssetNames: [] }))
        }
        choice={fields.fromChainName}
      />,
    ],
    [
      "currency_from",
      <AssetMultiSelection
        label={t("ph_currency_from")}
        setChoices={(fromAssetNames) =>
          setFields((f) => ({ ...f, fromAssetNames }))
        }
        choices={fields.fromAssetNames}
        allItems={allFromAssetNames}
      />,
    ],
    [
      "chain_name_to",
      <ChainSingleSelection
        label={t("ph_chain_name_to")}
        setChoice={(toChainName) =>
          setFields((f) => ({ ...f, toChainName, toAssetNames: [] }))
        }
        choice={fields.toChainName}
      />,
    ],
    [
      "currency_to",

      <AssetMultiSelection
        label={t("ph_currency_to")}
        setChoices={(toAssetNames) =>
          setFields((f) => ({ ...f, toAssetNames }))
        }
        choices={fields.toAssetNames}
        allItems={allToAssetNames}
      />,
    ],
    [
      "merchant_lp",
      <LPMerchantSingleSelection
        value={fields.merchantLp}
        onChange={(e) =>
          setFields((f) => ({ ...f, merchantLp: e.target.value }))
        }
        onClearSelect={() => setFields((f) => ({ ...f, merchantLp: "" }))}
      />,
    ],
    [
      "liquidity_provider",
      <NodeTypesEnumMultiSelection
        label={t("ph_liquidity_provider")}
        onChange={(liquidityProviders) =>
          setFields((f) => ({ ...f, liquidityProviders }))
        }
        value={fields.liquidityProviders}
        enumData={liquidityProvidersMapping}
        nameFn={(name) => name}
        isNoSorting
      />,
    ],
    [
      "merchant_order_id",
      <MpTextFieldMultiple
        value={fields.merchantOrderId}
        onChange={onChange("merchantOrderId")}
        label={t("ph_merchant_order_id")}
        count={apiParams.merchantOrderIds.length}
      />,
    ],

    [
      "request_status",
      <NodeTypesEnumMultiSelection
        label={t("ph_request_status")}
        onChange={(requestStatuses) =>
          setFields((f) => ({ ...f, requestStatuses }))
        }
        value={fields.requestStatuses}
        enumData={EnumRequestStatus}
        isNoSorting
      />,
    ],
    [
      "creator_type",

      <NodeTypesEnumSingleSelection
        value={fields.creatorType}
        label={t("ph_creator_type")}
        enumData={EnumCreatorTypeForDisplay}
        isNoSorting
        clearSelect={() => setFields((f) => ({ ...f, creatorType: "" }))}
        onChange={onChange("creatorType")}
      />,
    ],
  ]);
  return (
    <>
      <Box sx={customSx.gridFilter} className="gridFilter">
        {F.creation_time}
        {F.last_modified_time}
        {F.merchant_from}
        {F.merchant_to}
        {F.client_id_from}
        {F.client_id_to}
        {F.chain_name_from}
        {F.chain_name_to}
        {F.currency_from}
        {F.currency_to}
        {F.merchant_lp}
        {F.liquidity_provider}
        {F.merchant_order_id}
        {F.request_status}
        {F.creator_type}
      </Box>
      <Box sx={customSx.filterB}>
        <GeneralBtn label="search" onClick={onSearch} />
        <GeneralBtn label="reset" onClick={onReset} />
      </Box>
    </>
  );
}

const listMapping = (
  which: "key" | "name",
  array: any[],
  config?: {
    omitKeys: string[];
    t: any;
    te: any;
    merchantMapping: Record<string, string | number>;
  }
): any[] => {
  const { omitKeys = [], t, te } = config || {};

  const res = array.map((item: GetAllRes) => {
    const { name: fromChainName } =
      findChainInfo(String(item.from_chain_type), String(item.from_chain_id)) ||
      {};
    const fromDecimals = findDecimalByChainNameAndAsset(
      String(item.from_chain_type),
      String(item.from_chain_id),
      String(item.from_asset_name)
    );
    const { name: toChainName } =
      findChainInfo(String(item.to_chain_type), String(item.to_chain_id)) || {};
    const toDecimals = findDecimalByChainNameAndAsset(
      String(item.to_chain_type),
      String(item.to_chain_id),
      String(item.to_asset_name)
    );

    const getMerchantName = (merchantId: number) =>
      config?.merchantMapping?.[merchantId];

    const mappedResult = [
      ["merchant_order_id", t("merchant_order_id"), item.merchant_order_id],
      // [
      //   "approval_progress",
      //   t("approval_progress"),
      //   item.approvals_required === null
      //     ? null
      //     : !item.approvals_required
      //     ? t("auto_approve")
      //     : `${item.approvals}/${item.approvals_required}`,
      // ],
      [
        "approval_progress",
        t("approval_progress"),
        item.approvals_required === null
          ? null
          : !item.approvals_required
          ? t("auto_approve")
          : `${item.approvals}/${item.approvals_required}`,
      ],
      [
        "creator_type",
        t("creator_type"),
        te(EnumCreatorType[item.creator_type]),
      ],
      [
        "merchant_from",
        t("merchant_from"),
        getMerchantName(item.from_merchant_id),
      ],
      ["client_id_from", t("client_id_from"), item.from_client_id],
      ["chain_name_from", t("chain_name_from"), fromChainName],
      ["currency_from", t("currency_from"), item.from_asset_name],
      ["merchant_to", t("merchant_to"), getMerchantName(item.to_merchant_id)],
      ["client_id_to", t("client_id_to"), item.to_client_id],
      ["chain_name_to", t("chain_name_to"), toChainName],
      ["currency_to", t("currency_to"), item.to_asset_name],
      [
        "merchant_lp",
        t("merchant_lp"),
        getMerchantName(item.liquidity_provider_merchant_id),
      ],
      ["liquidity_provider", t("liquidity_provider"), item.liquidity_provider],
      ["exchange_rate", t("exchange_rate"), item.exchange_rate],
      [
        "transaction_amount_from",
        t("transaction_amount_from"),
        displayAmount(item.from_amount, fromDecimals),
      ],
      [
        "transaction_amount_to",
        t("transaction_amount_to"),
        displayAmount(item.to_amount, toDecimals),
      ],

      [
        "request_status",
        t("request_status"),
        te(EnumRequestStatus[item.status]),
      ],
      [
        "request_status",
        t("request_status"),
        te(EnumRequestStatus[item.status]),
      ],
      ["created_by", t("created_by"), item.created_by],
      ["creation_time", t("creation_time"), toDisplayTime(item.created_date)],
      [
        "last_modified_time",
        t("last_modified_time"),
        toDisplayTime(item.last_modified_date),
      ],
    ].filter(([key]) => !omitKeys.includes(key));
    return mappedResult;
  });

  const output = res.map(listMappingTransform(which));
  return output;
};

interface GetAllRes {
  id: number;
  from_merchant_id: number;
  from_chain_id: number;
  to_merchant_id: number;
  to_chain_id: number;
  path_id: number;
  liquidity_provider_merchant_id: number;
  merchant_order_id: string;
  from_client_id: string;
  from_chain_type: number;
  from_asset_name: string;
  from_amount: string;
  from_decimals: number;
  to_client_id: string;
  to_chain_type: number;
  to_asset_name: string;
  to_amount: string;
  to_decimals: number;
  exchange_rate: string;
  liquidity_provider: string;
  status: number;
  creator_type: number;
  created_by: string;
  created_date: string;
  last_modified_by: string;
  last_modified_date: string;
  approvals_required: null;
  approvals: number;
}
